import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faKey, faSpinner, faEye, faEyeSlash, faSort, faSortUp, faSortDown, faFileLines, faXmark, faUsers, faClipboardList, faSearch, faBell, faCode, faTv, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import ApiKeysPanel from '../components/admin/ApiKeysPanel';
import KioskDevicesPanel from '../components/admin/KioskDevicesPanel';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { userService, User, CreateUserData, UpdateUserData } from '../services/userService'
import { profileService, ContactEntry } from '../services/profileService'
import { permissionService, PermissionLevel, PermissionKey, PermissionAppFlag, getPermissionAppFlag, NotificationChannel } from '../services/permissionService';
import { platformContentService, PlatformContentData } from '../services/platformContentService';
import { notificationSettingsService } from '../services/notificationSettingsService';
import { hrToolsService } from '../services/hrToolsService';
import { portalMailService } from '../services/portalMailService';
import PositionSelect from '../components/PositionSelect';
import NotificationSettingsPanel from '../components/admin/NotificationSettingsPanel';
import { signaturePreviewDocument } from '../utils/emailSignatureHtml';
import { useAuth } from '../contexts/AuthContext';
import AuditLogsModal from '../components/AuditLogsModal'
import { useConfirmPassword } from '../components/ConfirmPasswordModal'
import { NOTIFICATION_TYPES, NotificationTypeKey, NotificationMatrixToggle, eventKeysForType, isNotificationTypeWired, isNotificationTypeFanOnly } from '../config/notificationTypes'

type AccessLevel = 'Admin' | 'Project Manager' | 'Crew Lead' | 'Crew Member' | 'AP/AR' | string;

const BUILT_IN_LEVELS: AccessLevel[] = ['Admin', 'Project Manager', 'Crew Lead', 'Crew Member', 'AP/AR'];

const _DEFAULT_PERMISSIONS: Record<AccessLevel, Record<string, boolean>> = {
  'Admin': {
    adminPage: true,
    gis: true,
    externalAdminApps: true,
    financials: true,
  },
  'Project Manager': {
    adminPage: false,
    gis: true,
    externalAdminApps: true,
    financials: false,
  },
  'Crew Lead': {
    adminPage: false,
    gis: false,
    externalAdminApps: false,
    financials: false,
  },
  'Crew Member': {
    adminPage: false,
    gis: false,
    externalAdminApps: false,
    financials: false,
  },
  'AP/AR': {
    adminPage: false,
    gis: false,
    externalAdminApps: true,
    financials: true,
  },
};

// Empty initial users - will be loaded from API
const initialUsers: User[] = [];

interface AdminPageProps {
}

type AdminTab = 'users' | 'permissions' | 'notifications' | 'email' | 'platformContent' | 'auditLogs' | 'apiKeys' | 'kioskDevices';
type UserSortKey = 'employeeId' | 'name' | 'position' | 'accessLevel' | 'lastLoginAt';

// Define a discriminated union for modal state
type ModalStateForState =
  | { type: 'add' }
  | { type: 'edit'; user: User }
  | { type: 'remove'; user: User }
  | { type: 'addLevel' }
  | { type: 'removeLevel'; level: PermissionLevel }
  | { type: null };

function conflictingUserByEmployeeId(
  users: User[],
  employeeId: string,
  excludeUserId?: string,
): User | undefined {
  const wanted = employeeId.trim()
  if (!wanted) return undefined
  return users.find((u) => {
    if (excludeUserId && u._id === excludeUserId) return false
    return String(u.employeeId || '').trim() === wanted
  })
}

const AdminPage: React.FC<AdminPageProps> = () => {
  const navigate = useNavigate()
  const { user: currentUser, loading: authLoading, hasPermission, applyAccessToken } = useAuth()
  const { confirmPassword, confirmPasswordModal } = useConfirmPassword()
  
  // Permission guard - redirect unauthorized users
  useEffect(() => {
    if (!authLoading && !hasPermission('adminPage')) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasPermission, navigate]);

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (will redirect)
  if (!hasPermission('adminPage')) {
    return null;
  }

  const [users, setUsers] = useState<User[]>(initialUsers);
  const [positionsByEmpId, setPositionsByEmpId] = useState<Record<string, string>>({});
  const [namesByEmpId, setNamesByEmpId] = useState<Record<string, string>>({});
  const [emailsByEmpId, setEmailsByEmpId] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState<UserSortKey>('employeeId');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [_permissions, _setPermissions] = useState<PermissionLevel[]>([]);
  const [form, setForm] = useState({ 
    employeeId: '', 
    username: '',
    name: '', 
    preferredName: '',
    position: '', 
    phone: '',
    email: '',
    workPhone: '',
    workEmail: '',
    department: '',
    supervisor: '',
    accessLevel: '',
    password: ''
  });

  // Permissions state
  const [permissionLevels, setPermissionLevels] = useState<PermissionLevel[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [newLevel, setNewLevel] = useState('');
  const [adminMfaStatus, setAdminMfaStatus] = useState<{ passkeyCount: number; totpEnabled: boolean; hasAnyMfa: boolean; remainingRecoveryCodes: number } | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [tempPasswordSending, setTempPasswordSending] = useState(false);
  const [tempPasswordNotice, setTempPasswordNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [modal, setModal] = useState<ModalStateForState>({ type: null });

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pw: string) => pw.length >= 8 },
    { label: 'One uppercase letter', test: (pw: string) => /[A-Z]/.test(pw) },
    { label: 'One lowercase letter', test: (pw: string) => /[a-z]/.test(pw) },
    { label: 'One number', test: (pw: string) => /[0-9]/.test(pw) },
    { label: 'One special character', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
  ];

  function getPasswordErrors(pw: string) {
    return passwordRequirements.filter(req => !req.test(pw)).map(req => req.label);
  }

  function downloadRecoveryCodesForAdmin(codes: string[], username?: string) {
    if (!codes?.length) return;
    const date = new Date().toISOString().slice(0, 10);
    const content = [
      'LTS Portal Recovery Codes',
      `Generated: ${new Date().toISOString()}`,
      `User: ${username || 'unknown'}`,
      '',
      'Sensitive security material.',
      'Share only with the user these codes belong to.',
      'Store securely and never distribute publicly.',
      '',
      ...codes
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lts-recovery-codes-${username || 'user'}-${date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Set default access level when permissionLevels change
  useEffect(() => {
    if (permissionLevels.length > 0 && !form.accessLevel) {
      setForm(f => ({ ...f, accessLevel: permissionLevels[0].name }));
    }
  }, [permissionLevels]);

  function applyContactMaps(contacts: ContactEntry[]) {
    const posMap: Record<string, string> = {};
    const nameMap: Record<string, string> = {};
    const emailMap: Record<string, string> = {};
    contacts.forEach(c => {
      if (!c.employeeId) return;
      posMap[c.employeeId] = c.position || '';
      nameMap[c.employeeId] = c.name || '';
      const address = String(c.workEmail || '').trim() || String(c.email || '').trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) emailMap[c.employeeId] = address;
    });
    setPositionsByEmpId(posMap);
    setNamesByEmpId(nameMap);
    setEmailsByEmpId(emailMap);
  }

  // Load users from API and hydrate positions from contacts
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [fetchedUsers, contacts] = await Promise.all([
          userService.getUsers(),
          profileService.getAllContacts().catch(() => []),
        ]);
        setUsers(fetchedUsers);
        applyContactMaps(contacts);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
        console.error('Error loading users:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Fetch permission levels from API
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setPermissionsLoading(true);
        setPermissionsError(null);
        const levels = await permissionService.getAll();
        setPermissionLevels(levels);
      } catch (err) {
        setPermissionsError(err instanceof Error ? err.message : 'Failed to load permissions');
      } finally {
        setPermissionsLoading(false);
      }
    };
    fetchPermissions();
  }, []);

  // Permission logic
  const currentLevel = permissionLevels.find(l => l.name === currentUser?.accessLevel);
  const canEdit = currentLevel && Array.isArray(currentLevel.permissions)
    ? currentLevel.permissions.includes('adminPage')
    : false;

  const filteredUsers = users.filter(user => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const displayName = namesByEmpId[user.employeeId] || user.name || '';
    const position = positionsByEmpId[user.employeeId] || user.title || '';
    const lastLogin = user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '';
    const haystack = [
      user.employeeId,
      displayName,
      user.username,
      position,
      user.accessLevel,
      lastLogin,
      user.lastLoginAt || '',
    ].join(' ').toLowerCase();
    return haystack.includes(q);
  }).sort((a, b) => {
    const posA = positionsByEmpId[a.employeeId] || a.title || ''
    const posB = positionsByEmpId[b.employeeId] || b.title || ''
    let va: string | number = ''
    let vb: string | number = ''
    if (sortBy === 'employeeId') {
      const na = Number(a.employeeId)
      const nb = Number(b.employeeId)
      if (!Number.isNaN(na) && !Number.isNaN(nb)) {
        va = na; vb = nb
      } else {
        va = a.employeeId; vb = b.employeeId
      }
    } else if (sortBy === 'name') {
      va = (namesByEmpId[a.employeeId] || a.name || a.username || '').toLowerCase();
      vb = (namesByEmpId[b.employeeId] || b.name || b.username || '').toLowerCase();
    } else if (sortBy === 'position') {
      va = posA.toLowerCase(); vb = posB.toLowerCase()
    } else if (sortBy === 'accessLevel') {
      va = a.accessLevel.toLowerCase(); vb = b.accessLevel.toLowerCase()
    } else if (sortBy === 'lastLoginAt') {
      va = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0
      vb = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0
    }
    let cmp = 0
    if (typeof va === 'number' && typeof vb === 'number') {
      cmp = va - vb
    } else {
      cmp = String(va).localeCompare(String(vb))
    }
    return sortDir === 'asc' ? cmp : -cmp
  });

  function toggleSort(col: UserSortKey) {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('asc')
    }
  }

  function sortIcon(col: UserSortKey) {
    if (sortBy !== col) {
      return <FontAwesomeIcon icon={faSort} className="opacity-30" />
    }
    return <FontAwesomeIcon icon={sortDir === 'asc' ? faSortUp : faSortDown} />
  }

  const news = [
    'LTS wins new telecom contract',
    'Quarterly safety training announced',
    'Employee of the Month: Jane Doe',
  ]

  const recentActivity = [
    'User John submitted a new form',
    'Site 12 updated by Admin',
    'GIS data refreshed',
    'Resource "Safety Manual" added',
  ]

  // Handlers for Add/Edit
  const openAddModal = () => {
    setError(null);
    setForm({ 
      employeeId: '', 
      username: '',
      name: '', 
      preferredName: '',
      position: '', 
      phone: '',
      email: '',
      workPhone: '',
      workEmail: '',
      department: '',
      supervisor: '',
      accessLevel: permissionLevels.length > 0 ? permissionLevels[0].name : '',
      password: ''
    });
    setModal({ type: 'add' });
  };

  const openEditModal = (user: User) => {
    setError(null);
    setForm({ 
      employeeId: user.employeeId,
      username: user.username || '',
      name: '',
      preferredName: '',
      position: '',
      phone: '',
      email: '',
      workPhone: '',
      workEmail: '',
      department: '',
      supervisor: '',
      accessLevel: user.accessLevel || '',
      password: ''
    });
    setTempPasswordNotice(null);
    setTempPasswordSending(false);
    setModal({ type: 'edit', user });
    setAdminMfaStatus(null);
    userService.adminMfaStatus(user._id).then(setAdminMfaStatus).catch(() => setAdminMfaStatus(null));
  };

  const employeeIdConflictUser = useMemo(() => {
    if (modal.type === 'edit' && 'user' in modal && modal.user) {
      return conflictingUserByEmployeeId(users, form.employeeId, modal.user._id)
    }
    if (modal.type === 'add') {
      return conflictingUserByEmployeeId(users, form.employeeId)
    }
    return undefined
  }, [form.employeeId, modal, users])

  const handleAddUser = async () => {
    if (employeeIdConflictUser || !form.employeeId.trim()) return
    try {
      const newUser = await userService.createUser({
        employeeId: form.employeeId,
        username: form.username,
        accessLevel: form.accessLevel,
        password: form.password,
      } as CreateUserData);
      // create Contact
      await profileService.createContact({
        employeeId: form.employeeId,
        name: form.name,
        preferredName: form.preferredName || undefined,
        position: form.position,
        email: form.email,
        phone: form.phone,
        workEmail: form.workEmail.trim() || undefined,
        workPhone: form.workPhone.trim() || undefined,
        department: form.department,
        supervisor: form.supervisor,
      });
      // create Profile
      await profileService.createProfile({ employeeId: form.employeeId });
      setUsers([newUser, ...users]);
      try {
        const contacts = await profileService.getAllContacts();
        applyContactMaps(contacts);
      } catch { /* best-effort */ }
      setModal({ type: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    }
  };

  const handleEditUser = async () => {
    if (modal.type !== 'edit') return;
    const user = modal.user;
    const nextEmployeeId = form.employeeId.trim();
    if (!nextEmployeeId || employeeIdConflictUser) return;
    if (nextEmployeeId !== String(user.employeeId || '').trim()) {
      const ok = window.confirm(
        `Change employee ID from ${user.employeeId} to ${nextEmployeeId}? This updates this person's records across the portal.`,
      );
      if (!ok) return;
    }
    try {
      if (form.password.trim() && getPasswordErrors(form.password).length) {
        setError('Password does not meet the strength requirements.');
        return;
      }
      const updatePayload: UpdateUserData = {
        employeeId: nextEmployeeId,
        username: form.username,
        accessLevel: form.accessLevel,
      };
      if (form.password.trim()) updatePayload.password = form.password;
      const { user: updatedUser, accessToken } = await userService.updateUser(user._id, updatePayload);
      setUsers(users.map(u => u._id === user._id ? updatedUser : u));
      if (accessToken) {
        await applyAccessToken(accessToken);
      }
      if (updatedUser.employeeId !== user.employeeId) {
        const remap = (prev: Record<string, string>) => {
          const next = { ...prev };
          if (Object.prototype.hasOwnProperty.call(next, user.employeeId)) {
            next[updatedUser.employeeId] = next[user.employeeId];
            delete next[user.employeeId];
          }
          return next;
        };
        setNamesByEmpId(remap);
        setPositionsByEmpId(remap);
        setEmailsByEmpId(remap);
      }
      setModal({ type: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const handleRemoveUser = async () => {
    if (modal.type !== 'remove') return;
    const user = modal.user;
    const label = namesByEmpId[user.employeeId] || user.name || user.username;
    // Close the "are you sure" dialog so the password step-up is the only modal open.
    setModal({ type: null });

    const deleted = await confirmPassword({
      title: 'Confirm password to delete user',
      description: `Re-enter your account password to permanently delete ${label} (${user.username}).`,
      confirmLabel: 'Delete user',
      onConfirm: async (currentPassword) => {
        await userService.deleteUser(user._id, currentPassword);
        return true;
      },
    });

    if (!deleted) return;
    setUsers(users.filter(u => u._id !== user._id));
  };

  const _handleAddLevel = async () => {
    if (!newLevel.trim()) return;
    try {
      const created = await permissionService.create({ name: newLevel.trim(), permissions: [] });
      setPermissionLevels([...permissionLevels, created]);
      setNewLevel('');
      setModal({ type: null });
    } catch (err) {
      setPermissionsError(err instanceof Error ? err.message : 'Failed to add access level');
    }
  };

  // Edit permissions for a level
  const handlePermissionChange = async (levelId: string, perm: string) => {
    const level = permissionLevels.find(l => l._id === levelId);
    if (!level) return;
    const hasPerm = level.permissions.includes(perm);
    const updatedPerms = hasPerm
      ? level.permissions.filter(p => p !== perm)
      : [...level.permissions, perm];
    try {
      const updated = await permissionService.update(levelId, { permissions: updatedPerms });
      setPermissionLevels(permissionLevels.map(l => l._id === levelId ? updated : l));
    } catch (err) {
      setPermissionsError(err instanceof Error ? err.message : 'Failed to update permissions');
    }
  };

  const levelNotificationChannels = (level: PermissionLevel): NotificationChannel[] =>
    Array.isArray(level.notifications) ? level.notifications : [];

  const findChannelEntry = (
    level: PermissionLevel,
    notificationKey: NotificationTypeKey,
    eventKey?: string | null,
  ) => {
    const channels = levelNotificationChannels(level);
    const allowed = eventKeysForType(notificationKey);
    if (allowed.length) {
      if (!eventKey) return undefined;
      return channels.find((c) => c.key === notificationKey && c.event === eventKey);
    }
    return channels.find((c) => c.key === notificationKey && !c.event);
  };

  const getChannelFlags = (
    level: PermissionLevel,
    key: NotificationTypeKey,
    eventKey?: string | null,
  ) => {
    const entry = findChannelEntry(level, key, eventKey);
    if (!entry) return { push: false, alert: false, email: false, user: false, fan: false };
    const legacyAudience = entry.user === undefined && entry.fan === undefined;
    if (isNotificationTypeFanOnly(key)) {
      return {
        push: !!entry.push,
        alert: !!entry.alert,
        email: !!entry.email,
        user: false,
        fan: true,
      };
    }
    return {
      push: !!entry.push,
      alert: !!entry.alert,
      email: !!entry.email,
      user: legacyAudience ? true : !!entry.user,
      fan: legacyAudience ? true : !!entry.fan,
    };
  };

  const buildChannelPayload = (
    notificationKey: NotificationTypeKey,
    push: boolean,
    alert: boolean,
    email: boolean,
    user: boolean,
    fan: boolean,
    eventKey?: string | null,
  ): NotificationChannel => {
    const fanOnly = isNotificationTypeFanOnly(notificationKey);
    const payload: NotificationChannel = {
      key: notificationKey,
      push,
      alert,
      email,
      user: fanOnly ? false : user,
      fan: fanOnly ? true : fan,
    };
    if (eventKey) payload.event = eventKey;
    return payload;
  };

  const sameChannelRow = (
    c: NotificationChannel,
    notificationKey: NotificationTypeKey,
    eventKey?: string | null,
  ) => {
    if (c.key !== notificationKey) return false;
    if (eventKey) return c.event === eventKey;
    return !c.event;
  };

  const handleNotificationChannelToggle = async (
    levelId: string,
    notificationKey: NotificationTypeKey,
    channel: NotificationMatrixToggle,
    eventKey?: string | null,
  ) => {
    const level = permissionLevels.find((l) => l._id === levelId);
    if (!level) return;
    if (!isNotificationTypeWired(notificationKey)) return;
    const fanOnly = isNotificationTypeFanOnly(notificationKey);
    if (fanOnly && channel === 'user') return;
    const allowed = eventKeysForType(notificationKey);
    if (allowed.length && (!eventKey || !allowed.includes(eventKey))) return;

    const current = levelNotificationChannels(level);
    const existing = findChannelEntry(level, notificationKey, eventKey);
    const flags = getChannelFlags(level, notificationKey, eventKey);
    let nextPush = flags.push;
    let nextAlert = flags.alert;
    let nextEmail = flags.email;
    let nextUser = fanOnly ? false : flags.user;
    let nextFan = fanOnly ? true : flags.fan;
    const deliveryOn = nextPush || nextAlert || nextEmail;

    if (channel === 'push') {
      nextPush = !nextPush;
      if (nextPush) {
        nextAlert = true;
        if (fanOnly) {
          nextUser = false;
          nextFan = true;
        } else if (!nextUser && !nextFan) {
          nextUser = true;
          nextFan = true;
        }
      }
    } else if (channel === 'alert') {
      nextAlert = !nextAlert;
      if (!nextAlert) nextPush = false;
      else if (fanOnly) {
        nextUser = false;
        nextFan = true;
      } else if (!nextUser && !nextFan) {
        nextUser = true;
        nextFan = true;
      }
    } else if (channel === 'email') {
      nextEmail = !nextEmail;
      if (nextEmail) {
        if (fanOnly) {
          nextUser = false;
          nextFan = true;
        } else if (!nextUser && !nextFan) {
          nextUser = true;
          nextFan = true;
        }
      }
    } else if (channel === 'user') {
      if (!deliveryOn) {
        nextUser = true;
        nextFan = false;
        nextAlert = true;
      } else {
        nextUser = !nextUser;
        if (!nextUser && !nextFan) nextFan = true;
      }
    } else if (channel === 'fan') {
      if (fanOnly) {
        // Fan-only rows: Fan is always on while delivery is on; toggling Fan off clears the row.
        if (!deliveryOn) {
          nextFan = true;
          nextUser = false;
          nextAlert = true;
        } else {
          nextPush = false;
          nextAlert = false;
          nextEmail = false;
        }
      } else if (!deliveryOn) {
        nextFan = true;
        nextUser = false;
        nextAlert = true;
      } else {
        nextFan = !nextFan;
        if (!nextFan && !nextUser) nextUser = true;
      }
    }

    if (fanOnly && (nextPush || nextAlert || nextEmail)) {
      nextUser = false;
      nextFan = true;
    }

    let updatedNotifications: NotificationChannel[];
    if (!nextPush && !nextAlert && !nextEmail) {
      updatedNotifications = current.filter((c) => !sameChannelRow(c, notificationKey, eventKey));
    } else {
      const payload = buildChannelPayload(
        notificationKey,
        nextPush,
        nextAlert,
        nextEmail,
        nextUser,
        nextFan,
        eventKey,
      );
      if (existing) {
        updatedNotifications = current.map((c) =>
          sameChannelRow(c, notificationKey, eventKey) ? payload : c,
        );
      } else {
        updatedNotifications = [...current, payload];
      }
    }

    try {
      const updated = await permissionService.update(levelId, { notifications: updatedNotifications });
      setPermissionLevels(permissionLevels.map((l) => (l._id === levelId ? updated : l)));
      setPermissionsError(null);
    } catch (err) {
      setPermissionsError(err instanceof Error ? err.message : 'Failed to update notification settings');
    }
  };

  // Remove access level
  const handleRemoveLevel = async (levelId: string) => {
    try {
      await permissionService.remove(levelId);
      setPermissionLevels(permissionLevels.filter(l => l._id !== levelId));
    } catch (err) {
      setPermissionsError(err instanceof Error ? err.message : 'Failed to remove access level');
    }
  };

  const passwordValid = !form.password || getPasswordErrors(form.password).length === 0;

  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [permissionsTab, setPermissionsTab] = useState<'accessLevels' | 'permissions'>('accessLevels');

  // Platform content state
  const [_platformContent, setPlatformContent] = useState<PlatformContentData | null>(null);
  const [platformContentLoading, setPlatformContentLoading] = useState(false);
  const [platformContentError, setPlatformContentError] = useState<string | null>(null);
  const [platformContentTab, setPlatformContentTab] = useState<'tos' | 'privacy' | 'updates'>('tos');
  const [platformContentForm, setPlatformContentForm] = useState({
    tos: { version: '', content: '' },
    privacy: { version: '', content: '' },
    updates: { version: '', content: '' },
  });
  const [platformContentSaving, setPlatformContentSaving] = useState<string | null>(null);

  // Shift reminder settings (global, below notification matrix)
  const [shiftSettingsForm, setShiftSettingsForm] = useState({
    clockOutAfterHours: 10,
    dailyFormsAfterHours: 5,
    dailyFormsFolders: [] as string[],
    reminderResendAfterHours: 2,
    reminderMaxSends: 3,
    folderDraft: '',
  });
  const [shiftSettingsLoading, setShiftSettingsLoading] = useState(false);
  const [shiftSettingsSaving, setShiftSettingsSaving] = useState(false);
  const [shiftSettingsError, setShiftSettingsError] = useState<string | null>(null);
  const [shiftSettingsSavedAt, setShiftSettingsSavedAt] = useState<string | null>(null);
  const [configuredPositions, setConfiguredPositions] = useState<string[]>([]);

  const [mailForm, setMailForm] = useState({ fromAddress: '', fromName: '', signatureHtml: '' });
  const [mailResolved, setMailResolved] = useState({
    resolvedFromAddress: '',
    resolvedFromName: 'LTS Portal',
    fromSource: 'none',
    googleConfigured: false,
  });
  const [mailLoading, setMailLoading] = useState(false);
  const [mailSaving, setMailSaving] = useState(false);
  const [mailError, setMailError] = useState<string | null>(null);
  const [mailSavedAt, setMailSavedAt] = useState<string | null>(null);
  const [mailTestTo, setMailTestTo] = useState('');
  const [mailTestSending, setMailTestSending] = useState(false);
  const [mailTestNotice, setMailTestNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Add state for all permissions
  const [allPermissions, setAllPermissions] = useState<PermissionKey[]>([]);
  const [permissionFlagSaving, setPermissionFlagSaving] = useState<string | null>(null);

  const handlePermissionAppFlagChange = async (key: string, flag: PermissionAppFlag) => {
    setPermissionFlagSaving(key);
    try {
      const updated = await permissionService.setPermissionAppFlag(key, flag);
      setAllPermissions((perms) =>
        perms.map((p) =>
          p.key === key
            ? { ...p, isExternalApp: !!updated.isExternalApp, isDevApp: !!updated.isDevApp }
            : p
        )
      );
      setPermissionsError(null);
    } catch (err) {
      setPermissionsError(err instanceof Error ? err.message : 'Failed to update permission flag');
    } finally {
      setPermissionFlagSaving(null);
    }
  };

  // Fetch all permissions on mount
  useEffect(() => {
    async function fetchAllPermissions() {
      try {
        const perms = await permissionService.getAllPermissions();
        setAllPermissions(perms);
      } catch (err) {
        setPermissionsError(err instanceof Error ? err.message : 'Failed to load permissions');
      }
    }
    fetchAllPermissions();
  }, []);

  const { internalPermissions, externalPermissions, devPermissions } = useMemo(() => {
    const internal: PermissionKey[] = [];
    const external: PermissionKey[] = [];
    const dev: PermissionKey[] = [];
    for (const perm of allPermissions) {
      const flag = getPermissionAppFlag(perm);
      if (flag === 'dev') dev.push(perm);
      else if (flag === 'external') external.push(perm);
      else internal.push(perm);
    }
    const byLabel = (a: PermissionKey, b: PermissionKey) =>
      String(a.label || a.key).localeCompare(String(b.label || b.key));
    internal.sort(byLabel);
    external.sort(byLabel);
    dev.sort(byLabel);
    return { internalPermissions: internal, externalPermissions: external, devPermissions: dev };
  }, [allPermissions]);

  const orderedPermissions = useMemo(
    () => [...internalPermissions, ...externalPermissions, ...devPermissions],
    [internalPermissions, externalPermissions, devPermissions]
  );

  // Load platform content when that tab is opened
  useEffect(() => {
    if (activeTab !== 'platformContent') return;
    let cancelled = false;
    (async () => {
      try {
        setPlatformContentLoading(true);
        setPlatformContentError(null);
        const data = await platformContentService.getAll();
        if (!cancelled) {
          setPlatformContent(data);
          setPlatformContentForm({
            tos: { version: data.tos.version, content: data.tos.content },
            privacy: { version: data.privacy.version, content: data.privacy.content },
            updates: { version: data.updates.version, content: data.updates.content },
          });
        }
      } catch (err) {
        if (!cancelled) {
          setPlatformContentError(err instanceof Error ? err.message : 'Failed to load platform content');
        }
      } finally {
        if (!cancelled) setPlatformContentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  // Load shift reminder settings when Notifications tab is opened
  useEffect(() => {
    if (activeTab !== 'notifications') return;
    let cancelled = false;
    (async () => {
      try {
        setShiftSettingsLoading(true);
        setShiftSettingsError(null);
        const data = await notificationSettingsService.get();
        if (!cancelled) {
          setShiftSettingsForm({
            clockOutAfterHours: data.clockOutAfterHours,
            dailyFormsAfterHours: data.dailyFormsAfterHours,
            dailyFormsFolders: data.dailyFormsFolders || [],
            reminderResendAfterHours: data.reminderResendAfterHours ?? 2,
            reminderMaxSends: data.reminderMaxSends ?? 3,
            folderDraft: '',
          });
          setShiftSettingsSavedAt(data.updatedAt);
        }
      } catch (err) {
        if (!cancelled) {
          setShiftSettingsError(err instanceof Error ? err.message : 'Failed to load shift reminder settings');
        }
      } finally {
        if (!cancelled) setShiftSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    hrToolsService.getPositions()
      .then((list) => { if (!cancelled) setConfiguredPositions(list); })
      .catch(() => { if (!cancelled) setConfiguredPositions([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTab !== 'email') return;
    let cancelled = false;
    (async () => {
      try {
        setMailLoading(true);
        setMailError(null);
        const data = await portalMailService.get();
        if (!cancelled) {
          setMailForm({
            fromAddress: data.fromAddress || '',
            fromName: data.fromName || '',
            signatureHtml: data.signatureHtml || '',
          });
          setMailResolved({
            resolvedFromAddress: data.resolvedFromAddress || '',
            resolvedFromName: data.resolvedFromName || 'LTS Portal',
            fromSource: data.fromSource || 'none',
            googleConfigured: !!data.googleConfigured,
          });
          setMailSavedAt(data.updatedAt);
          setMailTestTo((current) => current.trim() || data.resolvedFromAddress || data.fromAddress || '');
        }
      } catch (err) {
        if (!cancelled) {
          setMailError(err instanceof Error ? err.message : 'Failed to load email settings');
        }
      } finally {
        if (!cancelled) setMailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  function looksLikeEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  const mailFromInvalid = mailForm.fromAddress.trim().length > 0 && !looksLikeEmail(mailForm.fromAddress);

  const saveMailSettings = async () => {
    if (mailFromInvalid) {
      setMailError('From mailbox must be a valid email address.');
      return;
    }
    setMailSaving(true);
    setMailError(null);
    try {
      const saved = await portalMailService.update({
        fromAddress: mailForm.fromAddress.trim(),
        fromName: mailForm.fromName.trim(),
        signatureHtml: mailForm.signatureHtml,
      });
      setMailForm({
        fromAddress: saved.fromAddress,
        fromName: saved.fromName,
        signatureHtml: saved.signatureHtml || '',
      });
      setMailResolved({
        resolvedFromAddress: saved.resolvedFromAddress,
        resolvedFromName: saved.resolvedFromName,
        fromSource: saved.fromSource,
        googleConfigured: !!saved.googleConfigured,
      });
      setMailSavedAt(saved.updatedAt);
      setMailTestTo((current) => current.trim() || saved.resolvedFromAddress || '');
    } catch (err) {
      setMailError(err instanceof Error ? err.message : 'Failed to save email settings');
    } finally {
      setMailSaving(false);
    }
  };

  const sendMailTest = async () => {
    const to = mailTestTo.trim() || mailForm.fromAddress.trim() || mailResolved.resolvedFromAddress;
    setMailTestSending(true);
    setMailTestNotice(null);
    try {
      await portalMailService.sendTest(to);
      setMailTestNotice({ type: 'success', message: to ? `Test email sent to ${to}.` : 'Test email sent.' });
    } catch (err) {
      setMailTestNotice({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to send test email',
      });
    } finally {
      setMailTestSending(false);
    }
  };

  const closeModal = () => {
    setError(null);
    setModal({ type: null });
  };

  const handleSendTempPassword = async () => {
    if (modal.type !== 'edit' || !('user' in modal) || !modal.user) return;
    setTempPasswordSending(true);
    setTempPasswordNotice(null);
    try {
      const result = await userService.sendTempPassword(modal.user._id);
      setTempPasswordNotice({ type: 'success', message: result.message });
    } catch (err) {
      setTempPasswordNotice({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to email a temporary password',
      });
    } finally {
      setTempPasswordSending(false);
    }
  };

  const saveShiftReminderSettings = async () => {
    setShiftSettingsSaving(true);
    setShiftSettingsError(null);
    try {
      const updated = await notificationSettingsService.update({
        clockOutAfterHours: shiftSettingsForm.clockOutAfterHours,
        dailyFormsAfterHours: shiftSettingsForm.dailyFormsAfterHours,
        dailyFormsFolders: shiftSettingsForm.dailyFormsFolders,
        reminderResendAfterHours: shiftSettingsForm.reminderResendAfterHours,
        reminderMaxSends: shiftSettingsForm.reminderMaxSends,
      });
      setShiftSettingsForm((f) => ({
        ...f,
        clockOutAfterHours: updated.clockOutAfterHours,
        dailyFormsAfterHours: updated.dailyFormsAfterHours,
        dailyFormsFolders: updated.dailyFormsFolders || [],
        reminderResendAfterHours: updated.reminderResendAfterHours,
        reminderMaxSends: updated.reminderMaxSends,
      }));
      setShiftSettingsSavedAt(updated.updatedAt);
    } catch (err) {
      setShiftSettingsError(err instanceof Error ? err.message : 'Failed to save shift reminder settings');
    } finally {
      setShiftSettingsSaving(false);
    }
  };

  const savePlatformContent = async () => {
    const { version, content } = platformContentForm[platformContentTab];
    if (!version.trim()) return;
    setPlatformContentSaving(platformContentTab);
    try {
      const updated = await platformContentService.update(platformContentTab, version.trim(), content);
      setPlatformContent((p) =>
        p ? { ...p, [platformContentTab]: { version: updated.version, content: updated.content, updatedAt: updated.updatedAt ?? null } } : p
      );
      setPlatformContentForm((f) => ({
        ...f,
        [platformContentTab]: { version: updated.version, content: updated.content },
      }));
      setPlatformContentError(null);
    } catch (err) {
      setPlatformContentError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setPlatformContentSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="ADMIN" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1">
        <div role="tablist" className="tabs tabs-lift mb-6">
          <button
            role="tab"
            className={`tab ${activeTab === 'users' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FontAwesomeIcon icon={faUsers} className="mr-2" />
            Users
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'permissions' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            <FontAwesomeIcon icon={faKey} className="mr-2" />
            Permissions
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'notifications' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <FontAwesomeIcon icon={faBell} className="mr-2" />
            Notifications
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'email' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
            Email
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'platformContent' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('platformContent')}
          >
            <FontAwesomeIcon icon={faFileLines} className="mr-2" />
            Platform Content
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'auditLogs' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('auditLogs')}
          >
            <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
            Audit Logs
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'apiKeys' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('apiKeys')}
          >
            <FontAwesomeIcon icon={faCode} className="mr-2" />
            API
          </button>
          <button
            role="tab"
            className={`tab ${activeTab === 'kioskDevices' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('kioskDevices')}
          >
            <FontAwesomeIcon icon={faTv} className="mr-2" />
            Kiosk Devices
          </button>
        </div>

        {activeTab === 'users' && (
          <>
            <div className="flex flex-col md:flex-row gap-3 mb-4 items-stretch md:items-center">
              <label className="input input-bordered flex items-center gap-2 flex-1 min-w-0">
                <FontAwesomeIcon icon={faSearch} className="text-base-content/60" />
                <input
                  type="text"
                  className="grow"
                  placeholder="Search users by any field..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </label>
              {canEdit && (
                <button className="btn btn-primary shrink-0" onClick={openAddModal}>
                  <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add User
                </button>
              )}
            </div>

            {error && (
              <div className="alert alert-error mb-4">
                <span>{error}</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setError(null)} aria-label="Dismiss"><FontAwesomeIcon icon={faXmark} /></button>
              </div>
            )}

            {loading && (
              <div className="flex justify-center items-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="fa-spin text-2xl" />
                <span className="ml-2">Loading users...</span>
              </div>
            )}

            {!loading && (
              <div className="overflow-x-auto rounded-lg border border-base-300">
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th className="cursor-pointer select-none" onClick={() => toggleSort('employeeId')}>
                        <div className="inline-flex items-center gap-1">Employee ID {sortIcon('employeeId')}</div>
                      </th>
                      <th className="cursor-pointer select-none" onClick={() => toggleSort('name')}>
                        <div className="inline-flex items-center gap-1">Name {sortIcon('name')}</div>
                      </th>
                      <th className="cursor-pointer select-none" onClick={() => toggleSort('position')}>
                        <div className="inline-flex items-center gap-1">Position {sortIcon('position')}</div>
                      </th>
                      <th className="cursor-pointer select-none" onClick={() => toggleSort('accessLevel')}>
                        <div className="inline-flex items-center gap-1">Access Level {sortIcon('accessLevel')}</div>
                      </th>
                      <th className="cursor-pointer select-none" onClick={() => toggleSort('lastLoginAt')}>
                        <div className="inline-flex items-center gap-1">Last sign-in {sortIcon('lastLoginAt')}</div>
                      </th>
                      {canEdit && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user._id}>
                        <td>{user.employeeId}</td>
                        <td>{namesByEmpId[user.employeeId] || user.name || user.username}</td>
                        <td>{positionsByEmpId[user.employeeId] || user.title || ''}</td>
                        <td>{user.accessLevel}</td>
                        <td className="whitespace-nowrap text-sm opacity-90">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—'}
                        </td>
                        {canEdit && (
                          <td>
                            <button className="btn btn-xs btn-info mr-2" onClick={() => openEditModal(user)}>
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button className="btn btn-xs btn-error" onClick={() => setModal({ type: 'remove', user })}>
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'permissions' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">Manage Permissions</h2>
                <p className="text-sm text-base-content/70">Configure access levels and review available permission keys.</p>
              </div>
              {permissionsTab === 'accessLevels' && (
                <button className="btn btn-sm btn-primary self-start" onClick={() => setModal({ type: 'addLevel' })}>
                  <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add Level
                </button>
              )}
            </div>

            {permissionsError && (
              <div className="alert alert-error">
                <span>{permissionsError}</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPermissionsError(null)} aria-label="Dismiss"><FontAwesomeIcon icon={faXmark} /></button>
              </div>
            )}

            <div role="tablist" className="tabs tabs-boxed w-fit">
              <button
                type="button"
                role="tab"
                className={`tab ${permissionsTab === 'accessLevels' ? 'tab-active' : ''}`}
                onClick={() => setPermissionsTab('accessLevels')}
              >
                Access Levels
              </button>
              <button
                type="button"
                role="tab"
                className={`tab ${permissionsTab === 'permissions' ? 'tab-active' : ''}`}
                onClick={() => setPermissionsTab('permissions')}
              >
                Permissions
              </button>
            </div>

            {permissionsLoading ? (
              <div className="flex justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl animate-spin" />
              </div>
            ) : permissionsTab === 'accessLevels' ? (
              <div className="overflow-x-auto rounded-lg border border-base-300">
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th className="sticky left-0 z-20 bg-base-100" rowSpan={2}>Name</th>
                      {internalPermissions.length > 0 && (
                        <th colSpan={internalPermissions.length} className="text-center border-b border-base-300">
                          Portal apps
                        </th>
                      )}
                      {externalPermissions.length > 0 && (
                        <th
                          colSpan={externalPermissions.length}
                          className={`text-center border-b border-base-300 bg-base-200/40 ${
                            internalPermissions.length > 0 ? 'border-l-2 border-l-base-300' : ''
                          }`}
                        >
                          External apps
                        </th>
                      )}
                      {devPermissions.length > 0 && (
                        <th
                          colSpan={devPermissions.length}
                          className={`text-center border-b border-base-300 bg-warning/10 ${
                            internalPermissions.length > 0 || externalPermissions.length > 0
                              ? 'border-l-2 border-l-base-300'
                              : ''
                          }`}
                        >
                          Dev apps
                        </th>
                      )}
                      <th rowSpan={2} className="sticky right-0 z-20 bg-base-100 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.15)]"></th>
                    </tr>
                    <tr>
                      {orderedPermissions.map(perm => {
                        const flag = getPermissionAppFlag(perm);
                        const isFirstExternal =
                          flag === 'external' &&
                          internalPermissions.length > 0 &&
                          perm.key === externalPermissions[0]?.key;
                        const isFirstDev =
                          flag === 'dev' &&
                          (internalPermissions.length > 0 || externalPermissions.length > 0) &&
                          perm.key === devPermissions[0]?.key;
                        return (
                          <th
                            key={perm.key}
                            className={[
                              flag === 'external' ? 'bg-base-200/40' : '',
                              flag === 'dev' ? 'bg-warning/10' : '',
                              isFirstExternal || isFirstDev ? 'border-l-2 border-l-base-300' : '',
                            ].filter(Boolean).join(' ') || undefined}
                          >
                            {perm.label}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {permissionLevels.map(level => (
                      <tr key={level._id}>
                        <td className="sticky left-0 z-10 bg-base-100 font-semibold">{level.name}</td>
                        {orderedPermissions.map(perm => {
                          const flag = getPermissionAppFlag(perm);
                          const isFirstExternal =
                            flag === 'external' &&
                            internalPermissions.length > 0 &&
                            perm.key === externalPermissions[0]?.key;
                          const isFirstDev =
                            flag === 'dev' &&
                            (internalPermissions.length > 0 || externalPermissions.length > 0) &&
                            perm.key === devPermissions[0]?.key;
                          return (
                            <td
                              key={perm.key}
                              className={[
                                'text-center',
                                flag === 'external' ? 'bg-base-200/20' : '',
                                flag === 'dev' ? 'bg-warning/5' : '',
                                isFirstExternal || isFirstDev ? 'border-l-2 border-l-base-300' : '',
                              ].filter(Boolean).join(' ')}
                            >
                              <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={level.permissions.includes(perm.key)}
                                onChange={() => handlePermissionChange(level._id, perm.key)}
                              />
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 bg-base-100 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.15)]">
                          <button className="btn btn-xs btn-error" onClick={() => setModal({ type: 'removeLevel', level })}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-base-content/70">
                  Flag a permission as External (third-party dashboard tile) or Dev (developer tool). Leave as None for normal portal apps. Only one flag applies at a time.
                </p>
                <div className="max-h-[min(60vh,36rem)] overflow-auto overscroll-contain rounded-lg border border-base-300">
                  <table className="table w-full">
                    <thead className="sticky top-0 z-10 bg-base-200 shadow-sm">
                      <tr>
                        <th>Key</th>
                        <th>Label</th>
                        <th>Description</th>
                        <th className="whitespace-nowrap">Flag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderedPermissions.map(perm => {
                        const flag = getPermissionAppFlag(perm);
                        return (
                          <tr
                            key={perm.key}
                            className={
                              flag === 'external'
                                ? 'bg-base-200/20'
                                : flag === 'dev'
                                  ? 'bg-warning/5'
                                  : undefined
                            }
                          >
                            <td className="font-mono">{perm.key}</td>
                            <td>{perm.label}</td>
                            <td>{perm.description || '—'}</td>
                            <td>
                              <select
                                className="select select-bordered select-sm w-full max-w-[9rem]"
                                value={flag}
                                disabled={permissionFlagSaving === perm.key}
                                onChange={(e) =>
                                  handlePermissionAppFlagChange(perm.key, e.target.value as PermissionAppFlag)
                                }
                                aria-label={`Flag for ${perm.label}`}
                              >
                                <option value="none">None</option>
                                <option value="external">External</option>
                                <option value="dev">Dev</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                      {orderedPermissions.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center py-8 opacity-70">No permissions found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <NotificationSettingsPanel
            permissionLevels={permissionLevels}
            permissionsLoading={permissionsLoading}
            permissionsError={permissionsError}
            onDismissPermissionsError={() => setPermissionsError(null)}
            getChannelFlags={getChannelFlags}
            onToggle={(levelId, key, channel, eventKey) => {
              void handleNotificationChannelToggle(levelId, key, channel, eventKey)
            }}
            shiftSettingsForm={shiftSettingsForm}
            onShiftSettingsForm={(updater) => setShiftSettingsForm(updater)}
            shiftSettingsLoading={shiftSettingsLoading}
            shiftSettingsSaving={shiftSettingsSaving}
            shiftSettingsError={shiftSettingsError}
            shiftSettingsSavedAt={shiftSettingsSavedAt}
            onDismissShiftError={() => setShiftSettingsError(null)}
            onSaveShiftSettings={() => { void saveShiftReminderSettings() }}
          />
        )}

        {activeTab === 'email' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">System email</h2>
                <p className="text-sm text-base-content/70">
                  Outbound mail is sent through the Google Workspace service account, impersonating this mailbox.
                  Password emails and notification emails use this From address. If it is blank, the server uses PORTAL_MAIL_FROM.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-primary self-start shrink-0"
                disabled={mailLoading || mailSaving || mailFromInvalid}
                onClick={() => void saveMailSettings()}
              >
                {mailSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>

            {mailError && (
              <div className="alert alert-error">
                <span>{mailError}</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setMailError(null)} aria-label="Dismiss"><FontAwesomeIcon icon={faXmark} /></button>
              </div>
            )}

            {mailLoading ? (
              <div className="flex justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl animate-spin" />
              </div>
            ) : (
              <div className="rounded-lg border border-base-300 p-4 space-y-4">
                {!mailResolved.googleConfigured && (
                  <div className="alert alert-warning">
                    <span>Google Workspace mail is not configured on the server. Set the service account credentials before sending.</span>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="form-control w-full">
                    <span className="label-text">From mailbox</span>
                    <input
                      type="email"
                      className={`input input-bordered w-full ${mailFromInvalid ? 'input-error' : ''}`}
                      placeholder="hr@company.com"
                      value={mailForm.fromAddress}
                      onChange={(e) => setMailForm((f) => ({ ...f, fromAddress: e.target.value }))}
                      autoComplete="off"
                    />
                    <span className="label-text-alt text-base-content/60">
                      {mailResolved.resolvedFromAddress
                        ? `Currently sending as ${mailResolved.resolvedFromName} <${mailResolved.resolvedFromAddress}>${mailResolved.fromSource === 'env' ? ' (PORTAL_MAIL_FROM)' : ''}.`
                        : 'No From mailbox yet. Save an address here or set PORTAL_MAIL_FROM.'}
                    </span>
                  </label>
                  <label className="form-control w-full">
                    <span className="label-text">From display name</span>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      placeholder="LTS Portal"
                      maxLength={80}
                      value={mailForm.fromName}
                      onChange={(e) => setMailForm((f) => ({ ...f, fromName: e.target.value }))}
                    />
                    <span className="label-text-alt text-base-content/60">Blank uses LTS Portal</span>
                  </label>
                </div>

                <label className="form-control w-full">
                  <span className="label-text">Email signature / footer</span>
                  <textarea
                    className="textarea textarea-bordered w-full font-mono text-sm min-h-40"
                    placeholder={'<p>Larson Tower Specialists<br>HR</p>'}
                    value={mailForm.signatureHtml}
                    onChange={(e) => setMailForm((f) => ({ ...f, signatureHtml: e.target.value }))}
                    spellCheck={false}
                  />
                  <span className="label-text-alt text-base-content/60">
                    HTML is allowed, including tables, images, and inline styles. Replies go to the From mailbox.
                    Leave this blank for no signature.
                  </span>
                </label>
                {mailForm.signatureHtml.trim() ? (
                  <div className="space-y-2">
                    <span className="label-text">Preview</span>
                    <iframe
                      title="Email signature preview"
                      sandbox=""
                      className="w-full h-60 rounded border border-base-300 bg-white"
                      srcDoc={signaturePreviewDocument(mailForm.signatureHtml)}
                    />
                  </div>
                ) : null}

                <div className="border-t border-base-300 pt-4 space-y-3">
                  <div>
                    <h3 className="font-semibold">Send test email</h3>
                    <p className="text-sm text-base-content/70">Sends a short message through the configured mailbox. Defaults to the From address.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      className="input input-bordered flex-1"
                      placeholder={mailResolved.resolvedFromAddress || 'you@company.com'}
                      value={mailTestTo}
                      onChange={(e) => setMailTestTo(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={mailTestSending}
                      onClick={() => void sendMailTest()}
                    >
                      {mailTestSending ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        'Send test email'
                      )}
                    </button>
                  </div>
                  {mailTestNotice && (
                    <div className={`alert ${mailTestNotice.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                      <span>{mailTestNotice.message}</span>
                    </div>
                  )}
                  {mailSavedAt && (
                    <span className="text-xs text-base-content/50">
                      Last saved {new Date(mailSavedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'platformContent' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">Platform Content</h2>
                <p className="text-sm text-base-content/70">
                  Edit Terms of Service, Privacy Policy, and Platform Updates. Bumping the version number will require users to re-accept (TOS/Privacy) or re-view (Updates).
                </p>
              </div>
              <button
                className="btn btn-primary self-start shrink-0"
                disabled={platformContentLoading || !!platformContentSaving || !platformContentForm[platformContentTab].version.trim()}
                onClick={savePlatformContent}
              >
                {platformContentSaving === platformContentTab ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>

            {platformContentError && (
              <div className="alert alert-error">
                <span>{platformContentError}</span>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPlatformContentError(null)} aria-label="Dismiss"><FontAwesomeIcon icon={faXmark} /></button>
              </div>
            )}

            <div role="tablist" className="tabs tabs-boxed w-fit flex-wrap">
              {(['tos', 'privacy', 'updates'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  className={`tab ${platformContentTab === t ? 'tab-active' : ''}`}
                  onClick={() => setPlatformContentTab(t)}
                >
                  {t === 'tos' ? 'Terms of Service' : t === 'privacy' ? 'Privacy Policy' : 'Platform Updates'}
                </button>
              ))}
            </div>

            {platformContentLoading ? (
              <div className="flex justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl animate-spin" />
              </div>
            ) : (
              <div className="space-y-4 max-w-4xl">
                <div className="form-control">
                  <label className="label"><span className="label-text font-semibold">Version</span></label>
                  <input
                    className="input input-bordered w-full max-w-xs"
                    placeholder="e.g. 1 or 2"
                    value={platformContentForm[platformContentTab].version}
                    onChange={(e) =>
                      setPlatformContentForm((f) => ({
                        ...f,
                        [platformContentTab]: { ...f[platformContentTab], version: e.target.value },
                      }))
                    }
                  />
                  <p className="text-xs text-base-content/60 mt-1">Increment when you make changes. Users will see the new version.</p>
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text font-semibold">Content (Markdown)</span></label>
                  <textarea
                    className="textarea textarea-bordered w-full min-h-[360px] font-mono text-sm"
                    placeholder="Markdown content..."
                    value={platformContentForm[platformContentTab].content}
                    onChange={(e) =>
                      setPlatformContentForm((f) => ({
                        ...f,
                        [platformContentTab]: { ...f[platformContentTab], content: e.target.value },
                      }))
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'auditLogs' && (
          <AuditLogsModal active users={users} namesByEmpId={namesByEmpId} />
        )}

        {activeTab === 'apiKeys' && (
          <ApiKeysPanel />
        )}
        {activeTab === 'kioskDevices' && (
          <KioskDevicesPanel permissionLevels={permissionLevels} />
        )}

        {/* User / access-level modals */}
        {modal.type === 'add' && (
          <div className="modal modal-open">
            <div className="modal-box max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden shadow-2xl">
              <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-base-300">
                <h3 className="font-bold text-lg">Add User</h3>
                <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close" onClick={closeModal}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
                {permissionLevels.length === 0 ? (
                  <div className="alert alert-warning">No access levels available. Please add one in Manage Permissions first.</div>
                ) : (
                  <>
                    {error && (
                      <div className="alert alert-error mb-4">
                        <span>{error}</span>
                      </div>
                    )}
                    <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Username</span></label>
                      <input className="input input-bordered w-full" placeholder="Username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Access Level</span></label>
                      <select className="select select-bordered w-full" value={form.accessLevel} onChange={e => setForm(f => ({ ...f, accessLevel: e.target.value }))}>
                        {permissionLevels.map(level => <option key={level._id} value={level.name}>{level.name}</option>)}
                      </select>
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Employee ID</span></label>
                      <input
                        className={`input input-bordered w-full ${employeeIdConflictUser ? 'input-error' : ''}`}
                        placeholder="Employee ID"
                        value={form.employeeId}
                        onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}
                      />
                      {employeeIdConflictUser && (
                        <p className="text-error text-sm mt-1">
                          Employee ID {form.employeeId.trim()} is already assigned to {namesByEmpId[employeeIdConflictUser.employeeId] || employeeIdConflictUser.username}.
                        </p>
                      )}
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Name</span></label>
                      <input className="input input-bordered w-full" placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Preferred Name</span></label>
                      <input className="input input-bordered w-full" placeholder="Preferred Name" value={form.preferredName} onChange={e => setForm(f => ({ ...f, preferredName: e.target.value }))} />
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Position</span></label>
                      <PositionSelect
                        value={form.position}
                        positions={configuredPositions}
                        allowEmpty
                        emptyLabel="Select position"
                        onChange={(position) => setForm(f => ({ ...f, position }))}
                      />
                      {configuredPositions.length === 0 && (
                        <p className="text-xs text-base-content/60 mt-1">Add positions in HR → HR Tools before creating a user.</p>
                      )}
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Personal phone</span></label>
                      <input className="input input-bordered w-full" placeholder="Personal phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Personal email</span></label>
                      <input className="input input-bordered w-full" placeholder="Personal email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Work phone</span></label>
                      <input className="input input-bordered w-full" placeholder="Work phone (optional)" value={form.workPhone} onChange={e => setForm(f => ({ ...f, workPhone: e.target.value }))} />
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Work email</span></label>
                      <input className="input input-bordered w-full" placeholder="Work email (optional)" value={form.workEmail} onChange={e => setForm(f => ({ ...f, workEmail: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Department</span></label>
                        <input className="input input-bordered w-full" placeholder="Department" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} />
                      </div>
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Supervisor</span></label>
                        <input className="input input-bordered w-full" placeholder="Supervisor" value={form.supervisor} onChange={e => setForm(f => ({ ...f, supervisor: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-control">
                      <label className="label"><span className="label-text font-semibold">Password</span></label>
                      <div className="join w-full">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="input input-bordered join-item w-full"
                          placeholder="Password"
                          value={form.password}
                          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        />
                        <button
                          type="button"
                          className="btn btn-ghost join-item"
                          onClick={() => setShowPassword(v => !v)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                      {form.password && (
                        <ul className="mt-2 text-xs space-y-1">
                          {passwordRequirements.map(req => (
                            <li key={req.label} className={req.test(form.password) ? 'text-success' : 'text-error'}>
                              {req.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </form>
                  </>
                )}
              </div>
              <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-base-300 bg-base-100">
                <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddUser}
                  disabled={!!(
                    permissionLevels.length === 0 ||
                    !passwordValid ||
                    !form.username.trim() ||
                    !form.employeeId.trim() ||
                    !!employeeIdConflictUser ||
                    !form.name.trim() ||
                    !form.position.trim() ||
                    !form.email.trim() ||
                    !form.phone.trim() ||
                    !form.accessLevel.trim()
                  )}
                >Add</button>
              </div>
            </div>
            <div className="modal-backdrop" onClick={closeModal} />
          </div>
        )}
        {modal.type === 'edit' && 'user' in modal && modal.user && (
          <div className="modal modal-open">
            <div className="modal-box max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden shadow-2xl">
              <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-base-300">
                <div className="min-w-0">
                  <h3 className="font-bold text-lg">Edit User</h3>
                  <p className="text-sm text-base-content/60 truncate">{modal.user.username}</p>
                </div>
                <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close" onClick={closeModal}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-6">
                {permissionLevels.length === 0 ? (
                  <div className="alert alert-warning">No access levels available. Please add one in Manage Permissions first.</div>
                ) : (
                  <>
                    {error && (
                      <div className="alert alert-error">
                        <span>{error}</span>
                      </div>
                    )}
                    <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Username</span></label>
                        <input className="input input-bordered w-full" placeholder="Username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                      </div>
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Employee ID</span></label>
                        <input
                          className={`input input-bordered w-full ${employeeIdConflictUser ? 'input-error' : ''}`}
                          placeholder="Employee ID"
                          value={form.employeeId}
                          onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}
                        />
                        {employeeIdConflictUser && (
                          <p className="text-error text-sm mt-1">
                            Employee ID {form.employeeId.trim()} is already assigned to {namesByEmpId[employeeIdConflictUser.employeeId] || employeeIdConflictUser.username}.
                          </p>
                        )}
                        <p className="text-xs text-base-content/60 mt-1">
                          Changing this updates matching records across contacts, profiles, certifications, advances, forms, tally assignments, and audit history.
                        </p>
                      </div>
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Access Level</span></label>
                        <select className="select select-bordered w-full" value={form.accessLevel} onChange={e => setForm(f => ({ ...f, accessLevel: e.target.value }))}>
                          {permissionLevels.map(level => <option key={level._id} value={level.name}>{level.name}</option>)}
                        </select>
                      </div>
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Password</span></label>
                        <p className="text-sm text-base-content/70 mb-2">
                          Set a password directly, or email a temporary password. Leave this blank to keep the current password.
                        </p>
                        <div className="join w-full">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className="input input-bordered join-item w-full"
                            placeholder="New password"
                            value={form.password}
                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="btn btn-ghost join-item"
                            onClick={() => setShowPassword(v => !v)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            tabIndex={-1}
                          >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                          </button>
                        </div>
                        {form.password && (
                          <ul className="mt-2 text-xs space-y-1">
                            {passwordRequirements.map(req => (
                              <li key={req.label} className={req.test(form.password) ? 'text-success' : 'text-error'}>
                                {req.label}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-3">
                          {emailsByEmpId[modal.user.employeeId] ? (
                            <p className="text-sm text-base-content/70 mb-2">
                              Email a new temporary password to this user&apos;s work email, or their personal email if work email is empty. The current password stays in place if sending fails.
                            </p>
                          ) : (
                            <p className="text-sm text-warning mb-2">
                              No email on file. Add a work or personal email on the contact before sending a temporary password.
                            </p>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline btn-sm w-fit"
                            disabled={!emailsByEmpId[modal.user.employeeId] || tempPasswordSending}
                            onClick={() => void handleSendTempPassword()}
                          >
                            {tempPasswordSending ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                Sending...
                              </>
                            ) : (
                              'Email temporary password'
                            )}
                          </button>
                          {tempPasswordNotice && (
                            <div className={`alert mt-3 ${tempPasswordNotice.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                              <span>{tempPasswordNotice.message}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </form>
                    <div className="rounded-xl border border-base-300 bg-base-200/40 p-4 space-y-3">
                      <div>
                        <div className="font-semibold">Multi-Factor Authentication</div>
                        <div className="text-sm text-base-content/70">Manage MFA recovery and reset actions.</div>
                        {adminMfaStatus && (
                          <div className="text-xs text-base-content/70 mt-2">
                            Status: {adminMfaStatus.hasAnyMfa ? 'Enabled' : 'Not configured'} · Authenticator: {adminMfaStatus.totpEnabled ? 'Configured' : 'Not configured'} · Passkeys: {adminMfaStatus.passkeyCount} · Recovery codes: {adminMfaStatus.remainingRecoveryCodes}
                          </div>
                        )}
                        <div className="text-xs text-warning mt-2">Recovery codes are sensitive. Download and share only with the account owner.</div>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={async () => {
                            if (!('user' in modal) || !modal.user) return;
                            try {
                              const codes = await userService.adminRegenRecoveryCodes(modal.user._id)
                              alert('Recovery codes generated. Download and share securely with this user only.');
                              downloadRecoveryCodesForAdmin(codes, modal.user.username);
                              const st = await userService.adminMfaStatus(modal.user._id);
                              setAdminMfaStatus(st);
                            } catch (e: any) { alert(e?.message || 'Failed to regenerate codes') }
                          }}
                        >
                          Regenerate Recovery Codes
                        </button>
                        {adminMfaStatus?.passkeyCount ? (
                          <button
                            className="btn btn-sm btn-outline btn-warning"
                            onClick={async () => {
                              if (!('user' in modal) || !modal.user) return;
                              if (!confirm('Reset all passkeys for this user?')) return;
                              try {
                                await userService.adminResetPasskeys(modal.user._id);
                                alert('Passkeys reset for user');
                                const st = await userService.adminMfaStatus(modal.user._id);
                                setAdminMfaStatus(st);
                              } catch (e: any) { alert(e?.message || 'Failed to reset passkeys') }
                            }}
                          >
                            Reset Passkeys
                          </button>
                        ) : (
                          <span className="badge badge-ghost self-center">No passkeys enrolled</span>
                        )}
                        {adminMfaStatus?.totpEnabled ? (
                          <button
                            className="btn btn-sm btn-outline btn-warning"
                            onClick={async () => {
                              if (!('user' in modal) || !modal.user) return;
                              if (!confirm('Reset authenticator for this user? This removes authenticator setup and recovery codes.')) return;
                              try {
                                await userService.adminResetTotp(modal.user._id);
                                alert('Authenticator reset for user');
                                const st = await userService.adminMfaStatus(modal.user._id);
                                setAdminMfaStatus(st);
                              } catch (e: any) { alert(e?.message || 'Failed to reset authenticator') }
                            }}
                          >
                            Reset Authenticator
                          </button>
                        ) : (
                          <span className="badge badge-ghost self-center">Authenticator not configured</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-base-300 bg-base-100">
                <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleEditUser}
                  disabled={!!(
                    permissionLevels.length === 0 ||
                    !passwordValid ||
                    !form.employeeId.trim() ||
                    !!employeeIdConflictUser
                  )}
                >Save</button>
              </div>
            </div>
            <div className="modal-backdrop" onClick={closeModal} />
          </div>
        )}
        {modal.type === 'remove' && 'user' in modal && modal.user && (
          <div className="modal modal-open">
            <div className="modal-box max-w-md max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden shadow-2xl">
              <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-base-300">
                <h3 className="font-bold text-lg">Remove User</h3>
                <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close" onClick={closeModal}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
                <p>Are you sure you want to remove {(namesByEmpId[modal.user.employeeId] || modal.user.name || modal.user.username)} ({modal.user.username})?</p>
              </div>
              <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-base-300 bg-base-100">
                <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                <button className="btn btn-error" onClick={handleRemoveUser}>Remove</button>
              </div>
            </div>
            <div className="modal-backdrop" onClick={closeModal} />
          </div>
        )}
        {modal.type === 'addLevel' && (
          <div className="modal modal-open">
            <div className="modal-box max-w-md max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden shadow-2xl">
              <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-base-300">
                <h3 className="font-bold text-lg">Add Access Level</h3>
                <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close" onClick={closeModal}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <form
                className="min-h-0 flex-1 flex flex-col overflow-hidden"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newLevel.trim()) return;
                  try {
                    const created = await permissionService.create({ name: newLevel.trim(), permissions: [] });
                    setPermissionLevels([...permissionLevels, created]);
                    setNewLevel('');
                    setModal({ type: null });
                  } catch (err) {
                    setPermissionsError(err instanceof Error ? err.message : 'Failed to add access level');
                  }
                }}
              >
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-4">
                  {permissionsError && (
                    <div className="alert alert-error">
                      <span>{permissionsError}</span>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPermissionsError(null)} aria-label="Dismiss"><FontAwesomeIcon icon={faXmark} /></button>
                    </div>
                  )}
                  <div className="form-control">
                    <label className="label"><span className="label-text font-semibold">Access Level</span></label>
                    <input
                      className="input input-bordered w-full"
                      placeholder="Access Level"
                      value={newLevel}
                      onChange={e => setNewLevel(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-base-300 bg-base-100">
                  <button className="btn btn-ghost" type="button" onClick={closeModal}>Cancel</button>
                  <button className="btn btn-primary" type="submit" disabled={!newLevel.trim()}>Add</button>
                </div>
              </form>
            </div>
            <div className="modal-backdrop" onClick={closeModal} />
          </div>
        )}
        {modal.type === 'removeLevel' && modal.level && (
          <div className="modal modal-open">
            <div className="modal-box max-w-md max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden shadow-2xl">
              <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-base-300">
                <h3 className="font-bold text-lg">Remove Access Level</h3>
                <button type="button" className="btn btn-sm btn-circle btn-ghost" aria-label="Close" onClick={closeModal}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
                <p>Are you sure you want to remove the access level "{modal.level?.name}"?</p>
              </div>
              <div className="shrink-0 flex justify-end gap-2 px-6 py-4 border-t border-base-300 bg-base-100">
                <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
                <button
                  className="btn btn-error"
                  onClick={async () => {
                    if (modal.level) {
                      await handleRemoveLevel(modal.level._id);
                    }
                    setModal({ type: null });
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="modal-backdrop" onClick={closeModal} />
          </div>
        )}
      </main>
      <Footer />
      {confirmPasswordModal}
    </div>
  )
}


export default AdminPage 